import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { fetchMiscPurchaseOrderDetail } from '#/api/srm/miscPurchaseOrder/fetchDetail';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import { miscPurchaseOrderDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import MiscPurchaseOrderCommandBar from '../components/MiscPurchaseOrderCommandBar';
import MiscPurchaseOrderDetailTabs from '../components/MiscPurchaseOrderDetailTabs';
import MiscPurchaseOrderMainInfoCard from '../components/MiscPurchaseOrderMainInfoCard';
import { getMiscPurchaseOrderApiErrorMessage } from '../helper/apiError';

const MiscPurchaseOrderDetailPage = () => {
  const { id } = useParams();
  const detailQuery = useQuery({
    queryKey: miscPurchaseOrderDetailQueryKey(id),
    queryFn: () => fetchMiscPurchaseOrderDetail(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getMiscPurchaseOrderApiErrorMessage(
      detailQuery.error,
      '載入非庫存採購單失敗',
    ));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">非庫存採購單詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.miscPurchaseOrder}>返回列表</Link>
            </Button>
          </div>

          {detailQuery.isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getMiscPurchaseOrderApiErrorMessage(detailQuery.error, '載入非庫存採購單失敗')}
            </p>
          ) : null}

          {detail ? (
            <div className="space-y-4">
              <MiscPurchaseOrderMainInfoCard
                detail={detail}
                actions={(
                  <MiscPurchaseOrderCommandBar
                    key={`${detail.id}-${detail.record?.updatedAt ?? ''}`}
                    detail={detail}
                  />
                )}
              />
              <MiscPurchaseOrderDetailTabs
                detail={detail}
                onRefetch={() => detailQuery.refetch()}
              />
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default MiscPurchaseOrderDetailPage;
